import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import * as authApi from './authApi'
import type { AuthenticatedUser } from './authApi'
import { api } from '../../shared/api/http'

export type AccessContext = { clientOnly: boolean; hasScopedEventAccess:boolean; isGeneralAdministrator: boolean; hasCompanyAccess: boolean; accountAdministrator:boolean; accountRole:string|null;hasVigilanciaAccess:boolean;hasPortariaAccess:boolean }
const emptyAccess:AccessContext={clientOnly:false,hasScopedEventAccess:false,isGeneralAdministrator:false,hasCompanyAccess:false,accountAdministrator:false,accountRole:null,hasVigilanciaAccess:false,hasPortariaAccess:false}
type CompanyAccess={currentUserRole:string;hasScopedEventAccess?:boolean}
type ModuleAccess={vigilanciaAllowed:boolean;portariaAllowed:boolean}
async function resolveAccess():Promise<AccessContext>{
  let companies:CompanyAccess[]=[]
  try{companies=await api<CompanyAccess[]>('/companies')}catch{companies=[]}
  let modules:ModuleAccess[]=[]
  try{modules=await api<ModuleAccess[]>('/companies/modules/available')}catch{modules=[]}
  const isGeneralAdministrator=companies.some(x=>x.currentUserRole==='general_administrator')
  const accountRole=companies[0]?.currentUserRole??null
  const accountAdministrator=companies.some(x=>x.currentUserRole==='administrator')
  return {hasScopedEventAccess:companies.some(x=>x.hasScopedEventAccess),hasCompanyAccess:companies.length>0,clientOnly:companies.length>0&&companies.every(x=>x.currentUserRole==='client'),isGeneralAdministrator,accountAdministrator,accountRole,hasVigilanciaAccess:isGeneralAdministrator||accountAdministrator||modules.some(x=>x.vigilanciaAllowed),hasPortariaAccess:modules.some(x=>x.portariaAllowed)}
}

type AuthState = {
  loading: boolean
  user: AuthenticatedUser | null
  access: AccessContext
  login: typeof authApi.login
  register: typeof authApi.register
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState<AuthenticatedUser | null>(null)
  const [access,setAccess]=useState<AccessContext>(emptyAccess)

  useEffect(() => {
    let active = true
    authApi.restoreSession().then(async (restored) => {
      if (active) {
        setUser(restored)
        setAccess(restored?await resolveAccess():emptyAccess)
        setLoading(false)
      }
    })
    return () => { active = false }
  }, [])

  const value = useMemo<AuthState>(() => ({
    loading,
    user,
    access,
    login: async (...args) => {
      const authenticated = await authApi.login(...args)
      setUser(authenticated)
      setAccess(await resolveAccess())
      return authenticated
    },
    register: async (...args) => {
      const authenticated = await authApi.register(...args)
      setUser(authenticated)
      setAccess(await resolveAccess())
      return authenticated
    },
    logout: async () => {
      try {
        await authApi.logout()
      } catch {
        // authApi clears the local token even when the server is unavailable.
      } finally {
        sessionStorage.removeItem('spectra-module')
        setUser(null)
        setAccess(emptyAccess)
      }
    },
  }), [loading, user, access])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('AuthProvider ausente')
  return value
}
